import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getUiText, localeForLang } from './i18n'
import { VerifiedBadge, getCompanyBadgeVariant } from './VerifiedBadge'
import { HubbingIcon } from './icons'
import LoadingIndicator from './LoadingIndicator'
import RegistryCompaniesPanel from './RegistryCompaniesPanel'
import BusinessNewsPanel from './BusinessNewsPanel'
import ForumEntry from './ForumEntry'
import HomeSectionHeader from './HomeSectionHeader'
import { loadHomeData } from './homeData'
import { supabase } from './supabaseClient'
import { fetchCompanyDirectory } from './companyDirectory'
import { buildNeedOpportunities, getActiveNeedTags, getPermanentNeedText, getOpportunityLimit, parseCompanyNeedTags } from './needOpportunities'

const sectorColors = {
  'Fiduciaire & Comptabilité': '#2F7D32',
  'Fiduciaire': '#2F7D32',
  'Design & Créatif': '#7E3BB0',
  'Design & Communication': '#7E3BB0',
  'Informatique & Tech': '#1976D2',
  'Informatique': '#1976D2',
  'BTP & Construction': '#D32F2F',
  'Construction': '#D32F2F',
  'Marketing & Publicité': '#E91E63',
  'Marketing Digital': '#E91E63',
  'Ressources Humaines': '#0097A7',
  'Transport & Logistique': '#795548',
  'Commerce & Retail': '#FF8F00',
  'Immobilier': '#5D4037',
  'Finance & Assurance': '#388E3C',
  'Santé & Bien-être': '#00897B',
  'Éducation & Formation': '#5E35B1',
  'Juridique': '#455A64',
  'Industrie & Production': '#6D4C41',
  'Tourisme & Hôtellerie': '#039BE5',
  'Restauration': '#F4511E',
  'Nettoyage & Facility': '#00ACC1',
  'Sécurité': '#263238',
  'Événementiel': '#EC407A',
  'Consulting': '#3949AB',
  'Services': '#607D8B',
}

const sectors = Object.keys(sectorColors)
const swissCantons = ['AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH']
const opportunityFiltersInitial = { query: '', radius: 300, sector: '', canton: '' }
const homeStatsInitial = { matches: null, messages: null, followers: null, totalCompanies: null, collaborations: null, profileViews: null }
const statsSections = { matches: 'following', followers: 'followers', messages: 'messages', totalCompanies: 'totalCompanies', collaborations: 'collaborations', profileViews: 'profileViews' }
const homeStatusText = {
  fr: { retry: 'Réessayer', detailsUnavailable: 'Certaines informations sont temporairement indisponibles.', sectionUnavailable: 'Informations temporairement indisponibles.', viewsUnavailable: 'Compteur temporairement indisponible', companiesUnknown: 'Entreprises sur Hubbing · —', loadFailed: "Impossible de charger l'accueil", timeout: 'La connexion prend trop de temps. Réessayez.' },
  de: { retry: 'Erneut versuchen', detailsUnavailable: 'Einige Informationen sind vorübergehend nicht verfügbar.', sectionUnavailable: 'Informationen vorübergehend nicht verfügbar.', viewsUnavailable: 'Zähler vorübergehend nicht verfügbar', companiesUnknown: 'Unternehmen auf Hubbing · —', loadFailed: 'Die Startseite konnte nicht geladen werden', timeout: 'Die Verbindung dauert zu lange. Versuchen Sie es erneut.' },
  it: { retry: 'Riprova', detailsUnavailable: 'Alcune informazioni sono temporaneamente non disponibili.', sectionUnavailable: 'Informazioni temporaneamente non disponibili.', viewsUnavailable: 'Contatore temporaneamente non disponibile', companiesUnknown: 'Aziende su Hubbing · —', loadFailed: 'Impossibile caricare la pagina iniziale', timeout: 'La connessione impiega troppo tempo. Riprova.' },
  en: { retry: 'Retry', detailsUnavailable: 'Some information is temporarily unavailable.', sectionUnavailable: 'Information temporarily unavailable.', viewsUnavailable: 'Counter temporarily unavailable', companiesUnknown: 'Companies on Hubbing · —', loadFailed: 'Unable to load the home page', timeout: 'The connection is taking too long. Please retry.' },
}
const homeViewText = {
  fr:{viewsLabel:'Vues du profil · entreprises uniques',viewsHint:'Visites de votre profil. Une nouvelle visite de la même entreprise est comptabilisée après 15 minutes. Vos propres visites sont exclues.',connectionError:'Vérifiez votre connexion et réessayez.'},
  de:{viewsLabel:'Profilaufrufe · einzelne Unternehmen',viewsHint:'Aufrufe Ihres Profils. Ein erneuter Besuch desselben Unternehmens wird nach 15 Minuten gezählt. Eigene Besuche werden nicht berücksichtigt.',connectionError:'Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.'},
  it:{viewsLabel:'Visite al profilo · aziende uniche',viewsHint:'Visite al tuo profilo. Una nuova visita della stessa azienda viene conteggiata dopo 15 minuti. Le tue visite sono escluse.',connectionError:'Controlla la connessione e riprova.'},
  en:{viewsLabel:'Profile views · unique companies',viewsHint:'Visits to your profile. Another visit by the same company is counted after 15 minutes. Your own visits are excluded.',connectionError:'Check your connection and try again.'},
}
const normalizePlan = (value) => {
  const normalized = String(value || 'starter').toLowerCase()
  return normalized.includes('premium') ? 'premium' : normalized.includes('basic') ? 'basic' : 'starter'
}
const normalizeSearchText = (value = '') => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const getCompanyNeedsText = (company) => [getPermanentNeedText(company), ...getActiveNeedTags(company?.needs_tags).map(tag => tag.label)].filter(Boolean).join(' ')

const matchesOpportunityFilters = (opportunity, filters) => {
  const company = opportunity.company || {}
  if (filters.sector && company.sector !== filters.sector) return false
  if (filters.canton && company.canton !== filters.canton) return false
  if (filters.query) {
    const haystack = normalizeSearchText([
      company.name,
      company.sector,
      company.city,
      company.canton,
      getCompanyNeedsText(company),
      opportunity.matchReasons?.join(' '),
    ].filter(Boolean).join(' '))
    if (!haystack.includes(normalizeSearchText(filters.query).trim())) return false
  }
  if (Number(filters.radius) < 300 && typeof opportunity.distanceKm === 'number' && opportunity.distanceKm > Number(filters.radius)) {
    return false
  }
  return true
}

export default function HomeScreen(props) {
  return props.isSupportObserver
    ? <SupportObserverHome key={props.user.id} {...props}/>
    : <MemberHomeScreen {...props}/>
}

const observerHomeCopy={
  fr:{title:'Support Hubbing',mode:'Mode observation',description:'Consultez les entreprises et leurs profils publics. Ce compte interne ne possède pas de fiche entreprise.',directory:'Entreprises sur Hubbing',swipe:'Voir les entreprises',map:'Ouvrir la carte',back:'Retour à l’espace Support Hubbing',unavailable:'Annuaire temporairement indisponible.',retry:'Réessayer',empty:'Aucune entreprise disponible.'},
  de:{title:'Support Hubbing',mode:'Beobachtungsmodus',description:'Sehen Sie Unternehmen und ihre öffentlichen Profile an. Dieses interne Konto besitzt kein Unternehmensprofil.',directory:'Unternehmen auf Hubbing',swipe:'Unternehmen ansehen',map:'Karte öffnen',back:'Zurück zum Support Hubbing',unavailable:'Verzeichnis vorübergehend nicht verfügbar.',retry:'Erneut versuchen',empty:'Keine Unternehmen verfügbar.'},
  it:{title:'Support Hubbing',mode:'Modalità osservazione',description:'Consulta le aziende e i loro profili pubblici. Questo account interno non dispone di un profilo aziendale.',directory:'Aziende su Hubbing',swipe:'Vedi le aziende',map:'Apri la mappa',back:'Torna allo spazio Support Hubbing',unavailable:'Elenco temporaneamente non disponibile.',retry:'Riprova',empty:'Nessuna azienda disponibile.'},
  en:{title:'Support Hubbing',mode:'Observation mode',description:'View companies and their public profiles. This internal account has no company profile.',directory:'Companies on Hubbing',swipe:'View companies',map:'Open the map',back:'Return to the Support Hubbing workspace',unavailable:'Directory temporarily unavailable.',retry:'Retry',empty:'No companies available.'},
}

function SupportObserverHome({user,lang='fr',isActive=true,setActiveTab,setSelectedCompanyId,onReturnToSupport,onOpenForum}){
  const text=observerHomeCopy[lang]||observerHomeCopy.fr
  const [rows,setRows]=useState([]),[ready,setReady]=useState(false),[error,setError]=useState(false),[revision,setRevision]=useState(0)
  const [badgeIds,setBadgeIds]=useState(new Set())
  useEffect(()=>{
    if(!isActive)return undefined
    const controller=new AbortController();let current=true
    const timer=window.setTimeout(()=>{if(current){setError(true);controller.abort()}},12000)
    const load=async()=>{
      try{
        const directory=await fetchCompanyDirectory(supabase,{signal:controller.signal})
        if(!current||controller.signal.aborted)return
        setRows(directory);setReady(true);setError(false);setBadgeIds(new Set())
        // Only the public badge RPC is used. A failure never falls back to
        // private subscription tables or labels an unknown plan as Starter.
        const badges=[]
        for(let offset=0;offset<directory.length;offset+=500){
          const request=supabase.rpc('hubbing_company_badges',{p_company_ids:directory.slice(offset,offset+500).map(row=>row.id)})
          const result=await(typeof request.abortSignal==='function'?request.abortSignal(controller.signal):request)
          if(result.error)throw result.error
          badges.push(...(result.data||[]))
        }
        if(!current||controller.signal.aborted)return
        const known=new Map(badges.filter(row=>['starter','basic','premium'].includes(row.plan)).map(row=>[row.company_id,row]))
        setRows(directory.map(row=>({...row,subscriptions:known.has(row.id)?[{plan:known.get(row.id).plan,status:known.get(row.id).status||'active'}]:[]})))
        setBadgeIds(new Set(known.keys()))
      }catch(failure){if(current&&!controller.signal.aborted)setError(true)}
      finally{window.clearTimeout(timer)}
    }
    load()
    return()=>{current=false;controller.abort();window.clearTimeout(timer)}
  },[user.id,isActive,revision])
  const buttonStyle={padding:'12px 14px',border:'1px solid #cbd5e1',borderRadius:12,background:'white',color:'#334155',fontWeight:700,cursor:'pointer'}
  return <section style={{padding:'22px 16px calc(20px + env(safe-area-inset-bottom))'}}>
    <p style={{color:'#b4232e',fontSize:13,fontWeight:700,margin:'0 0 8px'}}>{text.mode}</p>
    <h1 style={{fontSize:25,margin:'0 0 12px'}}>{text.title}</h1>
    <p style={{color:'#475569',lineHeight:1.6}}>{text.description}</p>
    <div style={{display:'flex',gap:10,flexWrap:'wrap',margin:'20px 0'}}>
      <button type="button" style={buttonStyle} onClick={()=>setActiveTab?.('swipe')}>{text.swipe}</button>
      <button type="button" style={buttonStyle} onClick={()=>setActiveTab?.('map')}>{text.map}</button>
      {onReturnToSupport&&<button type="button" style={buttonStyle} onClick={onReturnToSupport}>{text.back}</button>}
    </div>
    <ForumEntry userId={user.id} lang={lang} isActive={isActive} onOpen={onOpenForum}/>
    <BusinessNewsPanel isActive={isActive} lang={lang}/>
    <RegistryCompaniesPanel userId={user.id} isActive={isActive} lang={lang}/>
    <h2 style={{fontSize:18}}>{text.directory}{ready?` · ${rows.length}`:' · —'}</h2>
    {!ready&&!error&&<LoadingIndicator label={getUiText(lang).common.loading}/>}
    {error&&<p role="status" style={{color:'#7f1d1d'}}>{text.unavailable} <button type="button" onClick={()=>{setError(false);setRevision(value=>value+1)}}>{text.retry}</button></p>}
    {ready&&rows.length===0&&<p>{text.empty}</p>}
    <div style={{display:'grid',gap:10}}>{rows.slice(0,12).map(row=>{
      const badge=badgeIds.has(row.id)?getCompanyBadgeVariant(row):null
      return <button type="button" key={row.id} onClick={()=>{setSelectedCompanyId?.(row.id);setActiveTab?.('home')}} style={{...buttonStyle,textAlign:'left',display:'flex',alignItems:'center',gap:12}}>
        {row.logo_url?<img src={row.logo_url} alt="" width="42" height="42" style={{borderRadius:10,objectFit:'contain'}}/>:<span aria-hidden="true" style={{width:42,height:42,display:'grid',placeItems:'center',borderRadius:10,background:'#f1f5f9'}}>{row.name?.slice(0,2).toUpperCase()}</span>}
        <span style={{flex:1}}><span style={{display:'flex',alignItems:'center',gap:6,fontSize:15}}>{row.name}{badge&&<VerifiedBadge variant={badge} lang={lang} size={16}/>}</span>
          <span style={{display:'block',fontSize:12,fontWeight:400,color:'#64748b',marginTop:4}}>{[row.sector,row.city].filter(Boolean).join(' · ')}</span></span>
      </button>
    })}</div>
  </section>
}

function MemberHomeScreen({ user, setActiveTab, setSelectedCompanyId, plan = 'Starter', lang = 'fr', isActive = true, companySnapshots = null, onOpenForum }) {
  const ui = getUiText(lang)
  const statusText = homeStatusText[lang] || homeStatusText.fr
  const viewText = homeViewText[lang] || homeViewText.fr
  const [company, setCompany] = useState(null)
  const [stats, setStats] = useState(homeStatsInitial)
  const [matchedCompanies, setMatchedCompanies] = useState([])
  const [followerCompanies, setFollowerCompanies] = useState([])
  const [opportunities, setOpportunities] = useState([])
  const [sections, setSections] = useState({})
  const [badgeCompanyIds, setBadgeCompanyIds] = useState(null)
  const [opportunityFilters, setOpportunityFilters] = useState(opportunityFiltersInitial)
  const [showOpportunityFilters, setShowOpportunityFilters] = useState(false)
  const [showFollowers, setShowFollowers] = useState(false)
  const [showFollowing, setShowFollowing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadErrorCode, setLoadErrorCode] = useState('')
  const isActiveRef = useRef(isActive)
  isActiveRef.current = isActive
  const identityRef = useRef(user.id)
  identityRef.current = user.id
  const loadSubscription = useRef(null)

  useEffect(() => {
    let active = true
    const refreshViews = async () => {
      if (document.hidden || !isActiveRef.current) return
      try {
        const {data,error} = await supabase.rpc('hubbing_profile_visit_count')
        if (active && identityRef.current === user.id) {
          const available = !error && data != null && Number.isFinite(Number(data))
          setStats(previous => ({...previous,profileViews:available ? Number(data) : null}))
          setSections(previous => ({...previous,profileViews:available ? 'ready' : 'error'}))
        }
      } catch {
        if (active && identityRef.current === user.id) {
          setStats(previous => ({...previous,profileViews:null}))
          setSections(previous => ({...previous,profileViews:'error'}))
        }
      }
    }
    window.addEventListener('focus',refreshViews)
    document.addEventListener('visibilitychange',refreshViews)
    const timer=window.setInterval(refreshViews,60000)
    return () => {active=false;window.clearInterval(timer);window.removeEventListener('focus',refreshViews);document.removeEventListener('visibilitychange',refreshViews)}
  }, [user.id])

  const loadGeneration = useRef(0)
  const loadedForUser = useRef(null)
  const previouslyActive = useRef(isActive)

  // A kept-mounted screen must never reuse private rows for another account.
  useEffect(() => {
    loadedForUser.current = null
    setCompany(null)
    setStats(homeStatsInitial)
    setMatchedCompanies([])
    setFollowerCompanies([])
    setOpportunities([])
    setSections({})
    setBadgeCompanyIds(null)
    setOpportunityFilters(opportunityFiltersInitial)
    setShowOpportunityFilters(false)
    setShowFollowers(false)
    setShowFollowing(false)
    setLoadErrorCode('')
    setLoading(true)
  }, [user.id])

  // Language only changes presentation. Keep in-flight/account-scoped data work
  // stable and store error codes so the current language is applied at render.
  const loadData = useCallback(async (background = false) => {
    if (!isActiveRef.current) return
    const previousSubscription = loadSubscription.current
    const controller = new AbortController()
    loadSubscription.current = controller
    const generation = ++loadGeneration.current
    const snapshotRevision = companySnapshots?.captureRevision()
    const requestScope = companySnapshots?.captureRequestScope()
    const hasPreviousData = background && loadedForUser.current === user.id
    let receivedData = false
    let ownSnapshotPublished = false
    let catalogSnapshotPublished = false
    const current = () => !controller.signal.aborted && generation === loadGeneration.current && identityRef.current === user.id
    if (!hasPreviousData) setLoading(true)
    const apply = data => {
      if (!current() || !data) return
      receivedData = true
      loadedForUser.current = user.id
      const nextSections = data.sections || {}
      if (!ownSnapshotPublished) {
        companySnapshots?.publishOwnCompany(user.id, data.company, snapshotRevision)
        ownSnapshotPublished = true
      }
      if (!catalogSnapshotPublished && nextSections.directory === 'ready' && nextSections.badges === 'ready') {
        companySnapshots?.publishCatalog(user.id, {
          company: data.company,
          directory: [data.company, ...(data.opportunityCompanies || [])],
        }, snapshotRevision)
        catalogSnapshotPublished = true
      }
      setLoadErrorCode('')
      setCompany(data.company)
      setSections(nextSections)
      setBadgeCompanyIds(data.badgeCompanyIds || null)
      setMatchedCompanies(previous => hasPreviousData && nextSections.following === 'loading' ? previous : data.followingMatches || [])
      setFollowerCompanies(previous => hasPreviousData && nextSections.followers === 'loading' ? previous : data.followerMatches || [])
      setStats(previous => Object.fromEntries(Object.keys(homeStatsInitial).map(key => [key,
        hasPreviousData && nextSections[statsSections[key]] === 'loading' ? previous[key] : data.stats?.[key] ?? null,
      ])))
      if (!hasPreviousData || nextSections.directory !== 'loading') {
        setOpportunities(buildNeedOpportunities(data.company, data.opportunityCompanies || [])
          .map(item => ({ ...item, attachmentCount: nextSections.attachments && nextSections.attachments !== 'ready'
            ? null : data.attachmentCounts?.[item.company.id] || 0 })))
      }
      setLoading(false)
    }
    try {
      const request = loadHomeData(user.id, { onUpdate: apply, signal: controller.signal, requestScope })
      previousSubscription?.abort()
      const data = await request
      if (!current()) return
      if (!data) throw Object.assign(new Error('Home company unavailable'),{code:'HOME_COMPANY_NOT_FOUND'})
      apply(data)
    } catch (error) {
      if (!current()) return
      console.warn('Unable to load home screen:', error)
      if (!receivedData && !hasPreviousData) {
        setOpportunities([])
        setLoadErrorCode(error?.code === 'HOME_TIMEOUT' ? 'timeout' : error?.code === 'HOME_COMPANY_NOT_FOUND' ? 'company_not_found' : 'generic')
      } else {
        setSections(previous => ({...previous, directory: 'error'}))
      }
    } finally {
      if (current()) setLoading(false)
    }
  }, [user.id, companySnapshots])

  useEffect(() => {
    if (isActiveRef.current) loadData(loadedForUser.current === user.id)
    const refresh = () => { if (!document.hidden && isActiveRef.current) loadData(true) }
    window.addEventListener('hubbing:companies-changed', refresh)
    return () => {
      loadGeneration.current += 1
      loadSubscription.current?.abort()
      window.removeEventListener('hubbing:companies-changed', refresh)
    }
  }, [loadData, user.id])

  useEffect(() => {
    if (!isActive) {
      loadGeneration.current += 1
      loadSubscription.current?.abort()
      setShowOpportunityFilters(false)
      setShowFollowers(false)
      setShowFollowing(false)
    } else if (!previouslyActive.current) {
      // Paint the cached screen immediately; refresh without replacing it with a loader.
      loadData(true)
    }
    previouslyActive.current = isActive
  }, [isActive, loadData])

  const getGreeting = () => {
    const h = new Date().getHours()
    if (h < 12) return ui.home.morning
    if (h < 18) return ui.home.afternoon
    return ui.home.evening
  }

  const color = company ? (sectorColors[company.sector] || '#E24B4A') : '#E24B4A'
  const companyBadgeVariant = getCompanyBadgeVariant(company, plan)
  const normalizedPlan = normalizePlan(plan)
  const planDisplayName = { starter: 'Starter', basic: 'Basic', premium: 'Premium' }[normalizedPlan] || 'Starter'
  const needsTags = parseCompanyNeedTags(company?.needs_tags)
  const onboardingSteps = [
    {
      key: 'profile',
      label: ui.home.onboardingProfile,
      done: Boolean(company?.sector && company?.description && company?.city),
      action: () => setActiveTab('profile'),
    },
    {
      key: 'visuals',
      label: ui.home.onboardingVisuals,
      done: Boolean(company?.logo_url || company?.contact_photo_url || company?.background_url),
      action: () => setActiveTab('profile'),
    },
    {
      key: 'needs',
      label: ui.home.onboardingNeeds,
      done: Boolean(company?.needs_description || needsTags.length > 0),
      action: () => setActiveTab('profile'),
    },
    {
      key: 'plan',
      label: ui.home.onboardingPlan,
      done: normalizedPlan !== 'starter',
      action: () => setActiveTab('pricing'),
    },
  ]
  const onboardingDone = onboardingSteps.filter(step => step.done).length
  const showOnboarding = onboardingDone < onboardingSteps.length
  const opportunityLimit = getOpportunityLimit(normalizedPlan)
  const filteredOpportunities = useMemo(
    () => opportunities.filter(item => matchesOpportunityFilters(item, opportunityFilters)),
    [opportunities, opportunityFilters]
  )
  const visibleOpportunities = useMemo(
    () => Number.isFinite(opportunityLimit) ? filteredOpportunities.slice(0, opportunityLimit) : filteredOpportunities,
    [filteredOpportunities, opportunityLimit]
  )
  const activeOpportunityFilterCount = [
    opportunityFilters.query,
    opportunityFilters.sector,
    opportunityFilters.canton,
    Number(opportunityFilters.radius) < 300 ? opportunityFilters.radius : '',
  ].filter(Boolean).length
  const resetOpportunityFilters = () => setOpportunityFilters(opportunityFiltersInitial)

  if (loading || (loadedForUser.current !== null && loadedForUser.current !== user.id)) return (
    <LoadingIndicator fill label={ui.common.loading} />
  )

  const loadErrorText = loadErrorCode === 'timeout' ? statusText.timeout : loadErrorCode === 'company_not_found' ? ui.common.companyNotFound : viewText.connectionError
  if (loadErrorCode) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',minHeight:420,padding:'2rem'}}>
      <div style={{width:'100%',maxWidth:360,textAlign:'center',background:'#FFF7F7',border:'1px solid #FECACA',borderRadius:14,padding:'1.25rem'}}>
        <p style={{fontSize:16,fontWeight:700,color:'#991B1B',margin:'0 0 0.5rem'}}>
          {statusText.loadFailed}
        </p>
        <p style={{fontSize:13,lineHeight:1.5,color:'#7F1D1D',margin:'0 0 1rem'}}>
          {loadErrorText}
        </p>
        <div style={{display:'flex',gap:10}}>
          <button type="button" onClick={() => loadData()} style={{flex:1,padding:'10px 12px',border:'none',borderRadius:10,background:'#E24B4A',color:'white',fontWeight:700,cursor:'pointer'}}>
            {statusText.retry}
          </button>
          <button type="button" onClick={() => setActiveTab('profile')} style={{flex:1,padding:'10px 12px',border:'1px solid #FECACA',borderRadius:10,background:'white',color:'#E24B4A',fontWeight:700,cursor:'pointer'}}>
            {ui.nav.profile}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
    {(showFollowers || showFollowing) && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:10000,paddingBottom:'calc(76px + env(safe-area-inset-bottom))'}}>
          <div role="dialog" aria-modal="true" aria-labelledby="home-relationship-modal-title" style={{background:'white',borderRadius:'20px 20px 0 0',width:'100%',maxWidth:430,maxHeight:'70vh',overflowY:'auto',padding:'1.5rem',paddingBottom:'calc(1.5rem + env(safe-area-inset-bottom))'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
              <h3 id="home-relationship-modal-title" style={{fontSize:18,fontWeight:700,margin:0}}>{showFollowers ? ui.home.followersTitle : ui.home.followingTitle}</h3>
              <button onClick={() => { setShowFollowers(false); setShowFollowing(false) }} style={{background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#999'}}>✕</button>
            </div>
            {sections[showFollowers ? 'followers' : 'following'] && sections[showFollowers ? 'followers' : 'following'] !== 'ready' ? (
              <HomeSectionStatus state={sections[showFollowers ? 'followers' : 'following']} loadingText={ui.common.loading} copy={statusText} onRetry={() => loadData(true)} />
            ) : (showFollowers ? followerCompanies : matchedCompanies).length === 0 ? (
              <p style={{color:'#999',textAlign:'center',padding:'2rem'}}>{showFollowers ? ui.home.noFollowers : ui.home.noFollowing}</p>
            ) : (
              (showFollowers ? followerCompanies : matchedCompanies).map(match => {
                const other = showFollowers ? match.company_a : match.company_b
                if (!other) return null
                const c = sectorColors[other.sector] || '#E24B4A'
                const otherBadgeVariant = (!badgeCompanyIds || badgeCompanyIds.includes(other.id)) && getCompanyBadgeVariant(other)
                return (
                  <div key={match.id} onClick={() => { setShowFollowers(false); setShowFollowing(false); setSelectedCompanyId && setSelectedCompanyId(other.id); setActiveTab('map') }} style={{padding:'0.75rem 0',display:'flex',alignItems:'center',gap:12,borderBottom:'1px solid #f5f5f5',cursor:'pointer'}}>
                    <div style={{width:44,height:44,borderRadius:'50%',background:c,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <span style={{color:'white',fontWeight:700,fontSize:14}}>{other.name?.substring(0,2).toUpperCase()}</span>
                    </div>
                    <div style={{flex:1}}>
                      <p style={{fontWeight:600,fontSize:14,margin:0,display:'flex',alignItems:'center',gap:5}}>
                        <span>{other.name}</span>
                        {otherBadgeVariant && <VerifiedBadge size={14} variant={otherBadgeVariant} lang={lang} />}
                      </p>
                      <p style={{fontSize:12,color:'#999',margin:0}}>{other.sector} · {other.city}</p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    {showOpportunityFilters && (
      <OpportunityFiltersModal
        ui={ui}
        filters={opportunityFilters}
        setFilters={setOpportunityFilters}
        activeFilterCount={activeOpportunityFilterCount}
        resultCount={filteredOpportunities.length}
        onReset={resetOpportunityFilters}
        onClose={() => setShowOpportunityFilters(false)}
      />
    )}
    <div style={{flex:1,overflowY:'auto',paddingBottom:'calc(18px + env(safe-area-inset-bottom))'}}>

{/* Header coloré */}
      <div style={{
        background: company?.background_url ? `url(${company.background_url}) center/cover no-repeat` : color,
        padding:'1.5rem',
        paddingBottom:'2.5rem',
        position:'relative'
      }}>
        {company?.background_url && (
          <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.35)',borderRadius:0}} />
        )}
        <div style={{position:'relative',zIndex:1}}>
        <p style={{color:'rgba(255,255,255,0.8)',fontSize:14,marginBottom:4}}>{getGreeting()}</p>
        <h2 style={{color:'white',fontSize:22,fontWeight:700,lineHeight:1.2,display:'flex',alignItems:'center',gap:7,flexWrap:'wrap'}}>
          <span>{company?.name || user.email}</span>
          {companyBadgeVariant && <VerifiedBadge size={22} variant={companyBadgeVariant} lang={lang} />}
        </h2>
        {company?.sector && (
          <p style={{color:'rgba(255,255,255,0.75)',fontSize:13,marginTop:4}}>
            {company.sector} · {company.city}, {company.canton}
          </p>
        )}
        <div style={{marginTop:12,background:'rgba(255,255,255,0.15)',borderRadius:8,padding:'6px 12px',display:'inline-block'}}>
          <span style={{color:'white',fontSize:12,fontWeight:600}}>
            <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
              <HubbingIcon name="building" size={14} color="white" />
              {stats.totalCompanies == null ? statusText.companiesUnknown : ui.home.companiesOnHubbing(stats.totalCompanies)}
            </span>
          </span>
        </div>
        </div>
      </div>

      {/* Stats cards: unique visiting companies, same counter as the website. */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(2, minmax(0, 1fr))',gap:10,padding:'0 1rem',marginTop:'-1.25rem',position:'relative',zIndex:1}}>
        <div onClick={() => setShowFollowing(true)} style={{flex:1,background:'white',borderRadius:12,padding:'0.875rem',textAlign:'center',boxShadow:'0 4px 16px rgba(0,0,0,0.08)',cursor:'pointer'}}>
          <p style={{fontSize:24,fontWeight:700,color:'#E24B4A',margin:0}}>{stats.matches ?? '—'}</p>
          <p style={{fontSize:11,color:'#999',marginTop:3}}>{ui.home.followingLabel}</p>
        </div>
        <div onClick={() => setShowFollowers(true)} style={{flex:1,background:'white',borderRadius:12,padding:'0.875rem',textAlign:'center',boxShadow:'0 4px 16px rgba(0,0,0,0.08)',cursor:'pointer'}}>
          <p style={{fontSize:24,fontWeight:700,color:'#E24B4A',margin:0}}>{stats.followers ?? '—'}</p>
          <p style={{fontSize:11,color:'#999',marginTop:3}}>{ui.home.followersLabel}</p>
        </div>
        <div style={{background:'white',borderRadius:12,padding:'0.875rem 0.65rem',textAlign:'center',boxShadow:'0 4px 16px rgba(0,0,0,0.08)'}}>
          <p style={{fontSize:24,fontWeight:700,color:'#E24B4A',margin:0}}>{stats.collaborations ?? '—'}</p>
          <p style={{fontSize:11,color:'#999',marginTop:3,lineHeight:1.2}}>{ui.home.collaborationsLabel}</p>
        </div>
        <div style={{background:'white',borderRadius:12,padding:'0.875rem',textAlign:'center',boxShadow:'0 4px 16px rgba(0,0,0,0.08)'}} title={viewText.viewsHint}>
          <p style={{fontSize:24,fontWeight:700,color:'#E24B4A',margin:0}}>{stats.profileViews == null ? '—' : stats.profileViews.toLocaleString(localeForLang(lang))}</p>
          <p style={{fontSize:11,color:'#777',marginTop:3}}>{viewText.viewsLabel}</p>
          {stats.profileViews == null && <small>{sections.profileViews === 'loading' ? ui.common.loading : statusText.viewsUnavailable}</small>}
        </div>
      </div>

      {Object.values(sections).includes('error') && (
        <div role="alert" style={{margin:'0.75rem 1rem',padding:12,borderRadius:10,background:'#FFF7ED',color:'#9A3412',fontSize:12}}>
          {statusText.detailsUnavailable}
          <button type="button" onClick={() => loadData(true)} style={{marginLeft:10,border:0,background:'transparent',color:'#9A3412',fontWeight:700,cursor:'pointer'}}>{statusText.retry}</button>
        </div>
      )}

      {showOnboarding && (
        <div style={{padding:'1rem 1rem 0'}}>
          <div style={{background:'white',border:'1px solid #F1D1D1',borderRadius:12,padding:'1rem',boxShadow:'0 4px 16px rgba(226,75,74,0.06)'}}>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'flex-start',marginBottom:10}}>
              <div>
                <p style={{fontWeight:800,fontSize:14,margin:0,color:'#1f2937'}}>{ui.home.onboardingTitle}</p>
                <p style={{fontSize:12,color:'#888',margin:'3px 0 0',lineHeight:1.35}}>{ui.home.onboardingDesc}</p>
              </div>
              <span style={{fontSize:11,fontWeight:800,color:'#E24B4A',background:'#FFF5F5',border:'1px solid #FECACA',borderRadius:999,padding:'5px 8px',whiteSpace:'nowrap'}}>
                {ui.home.onboardingProgress(onboardingDone, onboardingSteps.length)}
              </span>
            </div>
            <div style={{height:5,background:'#F5F5F5',borderRadius:999,overflow:'hidden',marginBottom:10}}>
              <div style={{width:`${(onboardingDone / onboardingSteps.length) * 100}%`,height:'100%',background:'#E24B4A',borderRadius:999}} />
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {onboardingSteps.map(step => (
                <button key={step.key} onClick={step.action}
                  style={{border:'1px solid #eee',background:step.done ? '#F0FDF4' : '#FAFAFA',borderRadius:10,padding:'9px 8px',display:'flex',alignItems:'center',gap:7,cursor:'pointer',textAlign:'left'}}>
                  <span style={{width:18,height:18,borderRadius:'50%',background:step.done ? '#22c55e' : 'white',border:`1px solid ${step.done ? '#22c55e' : '#ddd'}`,color:step.done ? 'white' : '#aaa',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,flexShrink:0}}>
                    {step.done ? '✓' : '•'}
                  </span>
                  <span style={{fontSize:11,fontWeight:700,color:step.done ? '#166534' : '#4b5563',lineHeight:1.25}}>{step.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{padding:'1.25rem 1rem 0.75rem',display:'flex',flexDirection:'column',gap:'1rem'}}>

        {/* Opportunités B2B */}
        <section className="hb-home-opportunities" style={{display:'flex',flexDirection:'column',gap:0,background:'white',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',boxShadow:'0 10px 24px rgba(17,24,39,0.08)'}}>
          <HomeSectionHeader
            title={ui.home.opportunitiesTitle}
            subtitle={ui.home.opportunitiesAllowance(Number.isFinite(opportunityLimit) ? opportunityLimit : null,planDisplayName)}
            level="h3"
            isActive={isActive}
            actions={
            <button
              type="button"
              onClick={() => setShowOpportunityFilters(true)}
              aria-label={ui.home.opportunitiesFilter}
              style={{
                minWidth:44,
                height:38,
                borderRadius:12,
                border:'1px solid #F1D1D1',
                background:activeOpportunityFilterCount ? '#FFF5F5' : 'white',
                color:'#E24B4A',
                display:'inline-flex',
                alignItems:'center',
                justifyContent:'center',
                gap:6,
                fontWeight:800,
                fontSize:12,
                cursor:'pointer',
                flexShrink:0,
                boxShadow:'0 4px 12px rgba(226,75,74,0.08)',
              }}
            >
              <HubbingIcon name="filters" size={18} color="#E24B4A" />
              {activeOpportunityFilterCount > 0 && (
                <span style={{minWidth:18,height:18,borderRadius:999,background:'#E24B4A',color:'white',fontSize:11,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>
                  {activeOpportunityFilterCount}
                </span>
              )}
            </button>
            }
          />

          <div className="hb-home-opportunities-body" style={{
            background:'white',
            border:0,
            borderRadius:0,
            padding:10,
            maxHeight:330,
            overflowY:'auto',
            WebkitOverflowScrolling:'touch',
          }}>
            {sections.directory && sections.directory !== 'ready' && visibleOpportunities.length === 0 ? (
              <HomeSectionStatus state={sections.directory} loadingText={ui.common.loading} copy={statusText} onRetry={() => loadData(true)} />
            ) : visibleOpportunities.length === 0 ? (
              <div style={{minHeight:128,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',padding:'1.25rem'}}>
                <div style={{width:42,height:42,borderRadius:'50%',background:'#FFF5F5',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:8}}>
                  <HubbingIcon name="briefcase" size={21} color="#E24B4A" />
                </div>
                <p style={{fontSize:14,fontWeight:800,color:'#1F2937',margin:'0 0 4px'}}>
                  {ui.home.opportunitiesEmpty}
                </p>
                <p style={{fontSize:12,color:'#9CA3AF',margin:0,lineHeight:1.35}}>
                  {ui.home.opportunitiesEmptyDesc}
                </p>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {visibleOpportunities.map(opportunity => (
                  <OpportunityCard
                    key={opportunity.id}
                    opportunity={opportunity}
                    lang={lang}
                    badgeReady={!badgeCompanyIds || badgeCompanyIds.includes(opportunity.company.id)}
                    ui={ui}
                    onOpen={(companyId) => {
                      setSelectedCompanyId && setSelectedCompanyId(companyId)
                      setActiveTab('map')
                    }}
                  />
                ))}
                {filteredOpportunities.length > visibleOpportunities.length && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('pricing')}
                    style={{
                      border:'1px solid #F1D1D1',
                      background:'#FFF7F7',
                      color:'#E24B4A',
                      borderRadius:12,
                      padding:'10px 12px',
                      fontSize:12,
                      fontWeight:800,
                      cursor:'pointer',
                    }}
                  >
                    {ui.home.opportunitiesUpgradeHint(filteredOpportunities.length - visibleOpportunities.length)}
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        <ForumEntry userId={user.id} lang={lang} isActive={isActive} onOpen={onOpenForum}/>

        <BusinessNewsPanel isActive={isActive} lang={lang}/>

        <RegistryCompaniesPanel userId={user.id} isActive={isActive} ownCompany={company} lang={lang}/>

        {/* Entreprises que je suis */}
        <HomeRelationshipSection
          title={ui.home.followingTitle}
          rows={matchedCompanies}
          companyKey="company_b"
          status={sections.following}
          statusText={statusText}
          loadingText={ui.common.loading}
          emptyText={ui.home.noConnections}
          emptyAction={<button type="button" onClick={() => setActiveTab('swipe')} style={{marginTop:8,padding:'8px 16px',background:'#E24B4A',color:'white',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>{ui.home.discoverCompanies}</button>}
          viewAllText={ui.home.viewAll}
          badgeCompanyIds={badgeCompanyIds}
          lang={lang}
          isActive={isActive}
          onViewAll={() => setShowFollowing(true)}
          onRetry={() => loadData(true)}
          onOpen={companyId => {setSelectedCompanyId && setSelectedCompanyId(companyId);setActiveTab('map')}}
        />

        {/* Entreprises qui me suivent : les relations entrantes utilisent company_a. */}
        <HomeRelationshipSection
          title={ui.home.followersTitle}
          rows={followerCompanies}
          companyKey="company_a"
          status={sections.followers}
          statusText={statusText}
          loadingText={ui.common.loading}
          emptyText={ui.home.noFollowers}
          viewAllText={ui.home.viewAll}
          badgeCompanyIds={badgeCompanyIds}
          lang={lang}
          isActive={isActive}
          onViewAll={() => setShowFollowers(true)}
          onRetry={() => loadData(true)}
          onOpen={companyId => {setSelectedCompanyId && setSelectedCompanyId(companyId);setActiveTab('map')}}
        />

        {/* Compléter profil si incomplet */}
        {(!company?.description || !company?.sector) && (
          <div onClick={() => setActiveTab('profile')}
            style={{background:'#f9f9f9',border:'1px solid #eee',borderRadius:12,padding:'1rem',cursor:'pointer',display:'flex',alignItems:'center',gap:12}}>
            <HubbingIcon name="pencil" size={28} color="#E24B4A" />
            <div style={{flex:1}}>
              <p style={{fontWeight:700,fontSize:14,margin:0}}>{ui.home.completeProfile}</p>
              <p style={{fontSize:12,color:'#999',marginTop:2}}>{ui.home.completeProfileDesc}</p>
            </div>
            <span style={{color:'#ccc',fontSize:18}}>›</span>
          </div>
        )}

        {/* Raccourci swipe */}
        <div onClick={() => setActiveTab('swipe')}
          style={{background:color,borderRadius:12,padding:'1.25rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <p style={{color:'white',fontWeight:700,fontSize:15,margin:0}}>{ui.home.discoverTitle}</p>
            <p style={{color:'rgba(255,255,255,0.75)',fontSize:12,marginTop:3}}>{ui.home.discoverDesc}</p>
          </div>
          <HubbingIcon name="briefcase" size={28} color="white" />
        </div>

      </div>
    </div>
    </>
  )
}

function HomeRelationshipSection({title,rows,companyKey,status,statusText,loadingText,emptyText,emptyAction,viewAllText,badgeCompanyIds,lang,isActive,onViewAll,onRetry,onOpen}) {
  const viewAllLabel = viewAllText.replace(/\s*→\s*$/,'')
  return <section className="hb-home-relationship" aria-label={title} style={{background:'white',border:'1px solid #f0f0f0',borderRadius:12,overflow:'hidden'}}>
    <HomeSectionHeader title={title} level="h3" isActive={isActive} actions={<button type="button" className="hb-home-section-action" aria-label={`${viewAllLabel} — ${title}`} onClick={onViewAll} style={{display:'inline-flex',alignItems:'center',gap:6}}>{viewAllLabel}<span aria-hidden="true" style={{display:'inline-flex'}}><HubbingIcon name="arrowRight" size={14} color="currentColor"/></span></button>}/>
    {status && status !== 'ready' && rows.length === 0 ? <HomeSectionStatus state={status} loadingText={loadingText} copy={statusText} onRetry={onRetry}/>
      : rows.length === 0 ? <div style={{padding:'1.5rem',textAlign:'center'}}><p style={{color:'#999',fontSize:13}}>{emptyText}</p>{emptyAction}</div>
      : <div>{rows.slice(0,4).map(match => {
        const other = match[companyKey]
        if (!other) return null
        const color = sectorColors[other.sector] || '#E24B4A'
        const badge = (!badgeCompanyIds || badgeCompanyIds.includes(other.id)) && getCompanyBadgeVariant(other)
        return <button type="button" key={match.id} onClick={() => onOpen(other.id)} style={{width:'100%',background:'white',border:'none',textAlign:'left',fontFamily:'inherit',padding:'0.75rem 1rem',display:'flex',alignItems:'center',gap:12,cursor:'pointer',borderBottom:'1px solid #f9f9f9'}}>
          <span aria-hidden="true" style={{width:40,height:40,borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><span style={{color:'white',fontWeight:700,fontSize:13}}>{other.name?.substring(0,2).toUpperCase()}</span></span>
          <span style={{flex:1,minWidth:0}}><span style={{fontWeight:600,fontSize:14,margin:0,display:'flex',alignItems:'center',gap:5}}><span>{other.name}</span>{badge && <VerifiedBadge size={14} variant={badge} lang={lang}/>}</span><span style={{display:'block',fontSize:12,color:'#999',margin:0}}>{other.sector} · {other.city}</span></span>
          <span aria-hidden="true" style={{color:'#ccc',fontSize:16}}>›</span>
        </button>
      })}</div>}
  </section>
}

function OpportunityCard({ opportunity, ui, onOpen, badgeReady = true, lang = 'fr' }) {
  const company = opportunity.company || {}
  const color = sectorColors[company.sector] || '#E24B4A'
  const badgeVariant = badgeReady && getCompanyBadgeVariant(company)
  const distanceLabel = typeof opportunity.distanceKm === 'number'
    ? ui.home.opportunityDistance(Math.max(1, Math.round(opportunity.distanceKm)))
    : ''
  const tags = opportunity.activeTags.slice(0, 2)
  const matchReasons = opportunity.matchReasons.slice(0, 3)

  return (
    <button
      type="button"
      onClick={() => onOpen(company.id)}
      style={{
        width:'100%',
        border:'1px solid #E5E7EB',
        background:'white',
        borderRadius:12,
        padding:12,
        display:'flex',
        alignItems:'flex-start',
        gap:10,
        textAlign:'left',
        cursor:'pointer',
      }}
    >
      <div style={{
        width:42,
        height:42,
        borderRadius:'50%',
        background:color,
        flexShrink:0,
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        overflow:'hidden',
      }}>
        {company.logo_url ? (
          <img src={company.logo_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
        ) : (
          <span style={{fontSize:13,fontWeight:800,color:'white'}}>
            {company.name?.substring(0, 2).toUpperCase() || 'HB'}
          </span>
        )}
      </div>

      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
          <p style={{fontSize:14,fontWeight:800,color:'#111827',margin:0,display:'flex',alignItems:'center',gap:5,minWidth:0}}>
            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{company.name}</span>
            {badgeVariant && <VerifiedBadge size={14} variant={badgeVariant} lang={lang} />}
          </p>
          <span style={{fontSize:11,fontWeight:800,color:'#E24B4A',whiteSpace:'nowrap'}}>
            {distanceLabel}
          </span>
        </div>
        <p style={{fontSize:11,color:'#9CA3AF',margin:'2px 0 7px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {[company.sector, company.city || company.canton].filter(Boolean).join(' · ')}
        </p>
        <p style={{
          fontSize:13,
          lineHeight:1.35,
          color:'#374151',
          margin:0,
          display:'-webkit-box',
          WebkitLineClamp:2,
          WebkitBoxOrient:'vertical',
          overflow:'hidden',
        }}>
          <strong style={{color:'#111827'}}>{ui.home.opportunityNeed}</strong>
          {' · '}
          {opportunity.title}
        </p>
        {(tags.length > 0 || matchReasons.length > 0 || opportunity.attachmentCount > 0) && (
          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:9}}>
            {matchReasons.map(reason => (
              <span key={`match-${reason}`} style={{fontSize:10,fontWeight:800,color:'#0F6E56',background:'#ECFDF5',border:'1px solid #A7F3D0',borderRadius:999,padding:'4px 7px'}}>
                {reason}
              </span>
            ))}
            {tags.map(tag => (
              <span key={tag.id || tag.label} style={{fontSize:10,fontWeight:800,color:'#9A3412',background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:999,padding:'4px 7px'}}>
                {tag.label}
              </span>
            ))}
            {opportunity.attachmentCount > 0 && (
              <span style={{fontSize:10,fontWeight:800,color:'#4B5563',background:'#F3F4F6',border:'1px solid #E5E7EB',borderRadius:999,padding:'4px 7px',display:'inline-flex',alignItems:'center',gap:4}}>
                <HubbingIcon name="paperclip" size={11} color="#4B5563" />
                {ui.home.opportunityAttachments(opportunity.attachmentCount)}
              </span>
            )}
          </div>
        )}
      </div>
      <span style={{fontSize:20,lineHeight:1,color:'#D1D5DB',paddingTop:8}}>›</span>
    </button>
  )
}

function OpportunityFiltersModal({ ui, filters, setFilters, activeFilterCount, resultCount, onReset, onClose }) {
  const setFilter = (key, value) => setFilters(current => ({ ...current, [key]: value }))

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(17,24,39,0.45)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:10000,paddingBottom:'calc(76px + env(safe-area-inset-bottom))'}}>
      <div style={{background:'white',borderRadius:'20px 20px 0 0',width:'100%',maxWidth:430,maxHeight:'76vh',overflowY:'auto',padding:'1.25rem',paddingBottom:'calc(1.25rem + env(safe-area-inset-bottom))',boxShadow:'0 -12px 40px rgba(0,0,0,0.18)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div>
            <h3 style={{fontSize:18,fontWeight:800,margin:0,color:'#111827'}}>{ui.home.opportunitiesFilterTitle}</h3>
            <p style={{fontSize:12,color:'#9CA3AF',margin:'3px 0 0'}}>
              {ui.home.opportunitiesFilterCount(resultCount)}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={ui.common.close} style={{width:34,height:34,borderRadius:'50%',border:'1px solid #E5E7EB',background:'#F9FAFB',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
            <HubbingIcon name="x" size={18} color="#4B5563" />
          </button>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <label style={{display:'flex',flexDirection:'column',gap:6}}>
            <span style={{fontSize:12,fontWeight:800,color:'#374151'}}>{ui.home.opportunitiesKeyword}</span>
            <input
              value={filters.query}
              onChange={(event) => setFilter('query', event.target.value)}
              placeholder={ui.home.opportunitiesKeywordPlaceholder}
              style={{width:'100%',boxSizing:'border-box',border:'1px solid #E5E7EB',borderRadius:12,padding:'12px 13px',fontSize:16,outline:'none'}}
            />
          </label>

          <label style={{display:'flex',flexDirection:'column',gap:8}}>
            <span style={{fontSize:12,fontWeight:800,color:'#374151'}}>
              {ui.home.opportunitiesRadius(Number(filters.radius))}
            </span>
            <input
              type="range"
              min="10"
              max="300"
              step="10"
              value={filters.radius}
              onChange={(event) => setFilter('radius', Number(event.target.value))}
              style={{accentColor:'#E24B4A'}}
            />
          </label>

          <label style={{display:'flex',flexDirection:'column',gap:6}}>
            <span style={{fontSize:12,fontWeight:800,color:'#374151'}}>{ui.home.opportunitiesSector}</span>
            <select
              value={filters.sector}
              onChange={(event) => setFilter('sector', event.target.value)}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:12,padding:'12px 13px',fontSize:16,background:'white'}}
            >
              <option value="">{ui.home.opportunitiesAllSectors}</option>
              {sectors.map(sector => <option key={sector} value={sector}>{sector}</option>)}
            </select>
          </label>

          <label style={{display:'flex',flexDirection:'column',gap:6}}>
            <span style={{fontSize:12,fontWeight:800,color:'#374151'}}>{ui.home.opportunitiesCanton}</span>
            <select
              value={filters.canton}
              onChange={(event) => setFilter('canton', event.target.value)}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:12,padding:'12px 13px',fontSize:16,background:'white'}}
            >
              <option value="">{ui.home.opportunitiesAllCantons}</option>
              {swissCantons.map(canton => <option key={canton} value={canton}>{canton}</option>)}
            </select>
          </label>
        </div>

        <div style={{display:'flex',gap:10,marginTop:18}}>
          <button type="button" onClick={onReset} disabled={activeFilterCount === 0} style={{flex:1,border:'1px solid #E5E7EB',background:'white',color:activeFilterCount ? '#4B5563' : '#CBD5E1',borderRadius:12,padding:'12px',fontSize:14,fontWeight:800,cursor:activeFilterCount ? 'pointer' : 'default'}}>
            {ui.home.opportunitiesReset}
          </button>
          <button type="button" onClick={onClose} style={{flex:1,border:'none',background:'#E24B4A',color:'white',borderRadius:12,padding:'12px',fontSize:14,fontWeight:800,cursor:'pointer'}}>
            {ui.home.opportunitiesShowResults(resultCount)}
          </button>
        </div>
      </div>
    </div>
  )
}


function HomeSectionStatus({ state, loadingText, copy, onRetry }) {
  return <div style={{padding:'1.5rem',textAlign:'center',fontSize:13,color:'#888'}}>
    <p>{state === 'loading' ? loadingText : copy.sectionUnavailable}</p>
    {state === 'error' && <button type="button" onClick={onRetry}>{copy.retry}</button>}
  </div>
}
